import React from "react"
import styles from "./Leaderboard.module.css"
import { ColDef } from "ag-grid-community"
// 添加以下依赖
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

function mean(array: Array<number>) {
  return array.reduce((a, b) => a + b, 0) / array.length
}

function formatNumber(number: number) {
  return Number(number.toFixed(2))
}

function formatVariance(number: number) {
  return Number(number.toFixed(4))
}

/**
 * 获取指定模型在特定级别、案例和复杂度的性能指标
 */
function getModelPerformance(
  results_df: Array<any>,
  model: string,
  selectedCase: string | null,
  selectedComplexity: number | null
) {
  // 按模型筛选数据 - 不再需要按level筛选，因为数据本身已经按level分开
  let results = results_df.filter(
    (result) => result["model"] === model
  );
  
  // 如果提供了案例和复杂度，进一步筛选
  if (selectedCase) {
    results = results.filter(result => result["case"] === selectedCase);
    
    if (selectedComplexity !== null) {
      results = results.filter(result => 
        result["complexity"] && 
        result["complexity"]["value"] === selectedComplexity
      );
    }
  }
  
  // 计算选定条件下的准确率和方差
  const accuracy = results.length > 0 
    ? formatNumber(mean(results.map(r => r["accuracy"] || 0))) 
    : 0;
  
  const variance = results.length > 0 
    ? formatVariance(mean(results.map(r => r["variance"] || 0)))
    : 0;

  return {
    accuracy,
    variance
  };
}

/**
 * 生成排行榜数据
 */
function getLeaderboard(
  performances: Array<any>,
  models: Array<any>,
  selectedLevel: number | undefined = undefined, // 修改为可选参数
  selectedCase: string | null = null,
  selectedComplexity: number | null = null
) {
  // 创建模型名称映射表和thinking映射表
  const modelInfoMap = models.reduce((map: any, model: any) => {
    // 裁剪可能的时间戳后缀
    const baseName = model.model_name?.split('-20')[0] || model.model_name;
    map[baseName] = {
      fullName: model.model_name,
      thinking: model.thinking
    };
    return map;
  }, {});

  // 获取数据中所有唯一的模型名称
  const uniqueModels = performances
    .filter(p => p.model) // 确保模型有名称
    .map(p => p.model)
    .filter((v, i, a) => a.indexOf(v) === i); // 去重

  return uniqueModels
    .map((modelName) => {
      const { 
        accuracy,
        variance
      } = getModelPerformance(
        performances,
        modelName,
        selectedCase,
        selectedComplexity
      );

      // 获取模型的thinking属性
      const modelInfo = modelInfoMap[modelName] || {};
      const isThinking = modelInfo.thinking === true;

      // 创建排行榜中的行数据
      return {
        Model: modelName,
        Accuracy: accuracy,
        Variance: variance,
        Thinking: isThinking // 添加Thinking字段
      };
    })
    // 按准确率降序排序
    .sort((a, b) => b.Accuracy - a.Accuracy)
    // 添加排名
    .reduce(
      (
        acc: {
          results: Array<any>
          rank: number
        },
        model
      ) => {
        acc.results.push({
          Rank: acc.rank,
          ...model,
        });
        acc.rank += 1;
        return acc;
      },
      { results: [], rank: 1 }
    ).results;
}


/**
 * 从难度级别数组生成标记
 */
function getDateMarksFromLevels(levels: Array<number> | undefined) {
  // 添加安全检查
  if (!levels || !Array.isArray(levels) || levels.length === 0) {
    // 提供默认值
    return [
      { value: 1, label: "Level 1" },
      { value: 2, label: "Level 2" },
      { value: 3, label: "Level 3" },
      { value: 4, label: "Level 4" }
    ];
  }
  
  return levels.map((level) => ({
    value: level,
    label: `Level ${level}`,
  }))
}

/**
 * 生成列定义
 */
function getColumnDefs(columnNames: Array<string>, modelsDict: any): ColDef[] {
  return columnNames
    .map((column_name): ColDef => {
      switch (column_name) {
        case "Rank":
          return {
            field: column_name,
            suppressMovable: true,
            cellClass: 'suppress-movable-col',
            width: 80,
            cellStyle: { textAlign: 'center' }, // 添加居中样式
            headerClass: 'centered-header'
          }

        case "Model":
          return {
            field: column_name,
            suppressMovable: true,
            cellClass: 'suppress-movable-col cell-centered', // 添加cell-centered类
            flex: 2,
            // 更明确的居中样式
            cellStyle: { 
              textAlign: 'center', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            },
            headerClass: 'centered-header',
            cellRenderer: (params: any) => {
              return params.value;
            },
          }

        case "Accuracy":
          return {
            field: column_name,
            headerTooltip: `Model accuracy for the selected difficulty level`,
            sort: 'desc' as const,
            flex: 1,
            valueFormatter: (params: any) => `${(params.value * 100).toFixed(2)}%`,
            cellStyle: { textAlign: 'center' }, // 添加居中样式
            headerClass: 'centered-header'
          }

        case "Variance":
          return {
            field: column_name,
            headerTooltip: "Variance of model performance",
            flex: 1,
            valueFormatter: (params: any) => params.value.toFixed(4),
            cellStyle: { textAlign: 'center' }, // 添加居中样式
            headerClass: 'centered-header'
          }
        
        case "Thinking":
          return {
            field: column_name,
            headerTooltip: "Whether the model uses step-by-step thinking",
            width: 100,
            cellStyle: { textAlign: 'center' }, // 确保居中样式正确
            headerClass: 'centered-header',
            cellRenderer: (params: any) => {
              return params.value ? '✓' : '✗';
            },
          }

        default:
          return {
            field: column_name,
            cellStyle: { textAlign: 'center' }, // 默认所有列都居中
            headerClass: 'centered-header'
          }
      }
    })
    .filter((columnDef): columnDef is ColDef => columnDef !== null)
}

function getDateMarksFromTimestamps(levels: Array<number> | undefined) {
  return getDateMarksFromLevels(levels);
}

export { getDateMarksFromTimestamps, getLeaderboard, getColumnDefs }